import { Moment, MOMENT_SLUGS } from '@/constants'
import type {
  AnnouncementBarData,
  ConfigurablePage,
  MomentKey,
  NavigationalBlocksData,
  PortalSection,
  PortalSettings,
  SectionKey,
  SectionsConfig,
  WelcomeMessageData,
} from '@/types'

const DEFAULT_NAV_LABELS: Required<NavigationalBlocksData> = {
  home_label: 'Home',
  orders_label: 'Orders',
  subscriptions_label: 'Subscriptions',
  address_payment_label: 'Account',
  log_out_label: 'Log Out',
  show_icons: true,
}

const DEFAULT_WELCOME: WelcomeMessageData = {
  heading: 'Welcome back, {first_name}',
  text_size: 'medium',
}

function parseConfig(portalSettings: PortalSettings | undefined): SectionsConfig | undefined {
  let config = portalSettings?.sections_config as any
  if (typeof config === 'string') {
    try {
      config = JSON.parse(config)
    } catch {
      return undefined
    }
  }
  return config as SectionsConfig | undefined
}

// Missing or unknown moment_status falls back to the `default` moment.
export function resolveMoment(session: any): MomentKey {
  return MOMENT_SLUGS[Number(session?.moment_status)] ?? MOMENT_SLUGS[Moment.None]
}

export function getHomeSections(portalSettings: PortalSettings | undefined, moment: MomentKey): PortalSection[] {
  const home = parseConfig(portalSettings)?.home
  return home?.[moment] ?? home?.[MOMENT_SLUGS[Moment.None]] ?? []
}

// Home is moment-keyed; every other page is a flat array.
function sectionsForPage(
  portalSettings: PortalSettings | undefined,
  page: ConfigurablePage,
  moment: MomentKey = MOMENT_SLUGS[Moment.None]
): PortalSection[] {
  if (page === 'home') {
    return getHomeSections(portalSettings, moment)
  }
  return (parseConfig(portalSettings)?.[page] as PortalSection[] | undefined) ?? []
}

export function getPageSections(portalSettings: PortalSettings | undefined, page: ConfigurablePage): PortalSection[] {
  return sectionsForPage(portalSettings, page)
}

export function findSection<T = Record<string, any>>(sections: PortalSection[], key: SectionKey): T | undefined {
  return sections.find(s => s.key === key)?.data as T | undefined
}

// Chrome is rendered by OmpLayout; everything else goes through the registry.
const CHROME_SECTION_KEYS: SectionKey[] = [
  'header',
  'announcement_bar',
  'welcome_message',
  'navigational_blocks',
  'footer',
]

function getContentSections(portalSettings: PortalSettings | undefined, page: ConfigurablePage): PortalSection[] {
  return getPageSections(portalSettings, page).filter(s => !CHROME_SECTION_KEYS.includes(s.key))
}

// Guarantees the page's list section exists; a config omitting it would render empty.
export function getListPageSections(
  portalSettings: PortalSettings | undefined,
  page: ConfigurablePage,
  listKey: SectionKey
): PortalSection[] {
  const sections = getContentSections(portalSettings, page)
  if (sections.some(s => s.key === listKey)) {
    return sections
  }
  return [{ key: listKey, data: {}, is_visible: true }, ...sections]
}

export function resolveNavLabels(
  portalSettings: PortalSettings | undefined,
  page: ConfigurablePage = 'home',
  moment: MomentKey = MOMENT_SLUGS[Moment.None]
): Required<NavigationalBlocksData> {
  const data =
    findSection<NavigationalBlocksData>(sectionsForPage(portalSettings, page, moment), 'navigational_blocks') ??
    findSection<NavigationalBlocksData>(getHomeSections(portalSettings, moment), 'navigational_blocks')
  return { ...DEFAULT_NAV_LABELS, ...(data ?? {}) }
}

// Returns the current page's announcement_bar section (with is_visible), or undefined.
export function resolveAnnouncement(
  portalSettings: PortalSettings | undefined,
  page: ConfigurablePage = 'home',
  moment: MomentKey = MOMENT_SLUGS[Moment.None]
): PortalSection<AnnouncementBarData> | undefined {
  return sectionsForPage(portalSettings, page, moment).find(s => s.key === 'announcement_bar') as
    | PortalSection<AnnouncementBarData>
    | undefined
}

// Home only — the editor strips welcome_message from every other page.
export function resolveWelcome(
  portalSettings: PortalSettings | undefined,
  page: ConfigurablePage = 'home',
  moment: MomentKey = MOMENT_SLUGS[Moment.None]
): { data: WelcomeMessageData; visible: boolean } {
  if (page !== 'home') {
    return { data: DEFAULT_WELCOME, visible: false }
  }
  const section = getHomeSections(portalSettings, moment).find(s => s.key === 'welcome_message')
  return {
    data: { ...DEFAULT_WELCOME, ...(section?.data ?? {}) },
    visible: section?.is_visible !== false,
  }
}
